<template>
  <v-card class="incident-inbox">
    <v-card-title class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-inbox</v-icon>
        <span>{{ inboxTitle }}</span>
        <v-chip class="ml-2" size="small" color="primary" variant="tonal">
          {{ totalCount }}
        </v-chip>
      </div>

      <div class="d-flex align-center ga-2">
        <!-- Filtro por estado -->
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-title="label"
          item-value="value"
          label="Estado"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          style="max-width: 200px"
        />
        <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="loadData('manual-refresh')" />
      </div>
    </v-card-title>

    <v-divider />

    <!-- Loading -->
    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <!-- Error -->
    <v-alert v-if="error" type="error" class="ma-4" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <!-- Empty state -->
    <v-card-text v-if="!loading && !error && incidents.length === 0">
      <div class="text-center py-8">
        <v-icon size="64" color="grey-lighten-1">mdi-inbox-outline</v-icon>
        <p class="text-body-1 text-grey mt-4">No hay incidencias en esta bandeja</p>
      </div>
    </v-card-text>

    <!-- Lista de incidencias -->
    <v-list v-if="incidents.length > 0" lines="three">
      <template v-for="(incident, index) in incidents" :key="incident.id">
        <v-list-item @click="selectedIncident = incident">
          <template #prepend>
            <v-avatar :color="priorityColor(incident.priority)" size="40">
              <v-icon color="white" size="20">{{ priorityIcon(incident.priority) }}</v-icon>
            </v-avatar>
          </template>

          <v-list-item-title class="font-weight-medium">
            #{{ incident.id }} — {{ incident.title }}
          </v-list-item-title>

          <v-list-item-subtitle>
            <div class="d-flex align-center ga-2 flex-wrap mt-1">
              <v-chip :color="statusColor(incident.status)" size="x-small" variant="flat">
                {{ statusLabel(incident.status) }}
              </v-chip>
              <v-chip size="x-small" variant="outlined" v-if="incident.assignedDepartment">
                <v-icon start size="12">mdi-domain</v-icon>
                {{ incident.assignedDepartment.name }}
              </v-chip>
              <v-chip size="x-small" variant="outlined" v-if="incident.reportedByUser">
                <v-icon start size="12">mdi-account</v-icon>
                {{ incident.reportedByUser.name }}
              </v-chip>
              <span class="text-caption text-grey">
                {{ formatDate(incident.createdAt) }}
              </span>
              <!-- Lote 4.4: Badge de nivel de escalación -->
              <v-chip
                v-if="(incident as any).activeEscalations?.length"
                size="x-small"
                color="warning"
                variant="flat"
              >
                <v-icon start size="12">mdi-arrow-up-bold</v-icon>
                Nivel {{ Math.max(...(incident as any).activeEscalations.map((e: any) => e.level)) }}
              </v-chip>
            </div>
          </v-list-item-subtitle>

          <template #append>
            <!-- Acciones disponibles del state machine -->
            <div class="d-flex ga-1" v-if="incident.availableActions?.length">
              <v-tooltip
                v-for="act in incident.availableActions"
                :key="act.action"
                :text="act.label"
                location="top"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    :icon="actionIcon(act.action)"
                    :color="actionColor(act.action)"
                    size="small"
                    variant="tonal"
                    @click.stop="openActionDialog(incident, act)"
                  />
                </template>
              </v-tooltip>
            </div>
          </template>
        </v-list-item>

        <v-divider v-if="index < incidents.length - 1" />
      </template>
    </v-list>

    <!-- Paginación -->
    <v-card-actions v-if="totalPages > 1" class="justify-center">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="5"
        density="compact"
        @update:model-value="loadData('pagination')"
      />
    </v-card-actions>

    <!-- Dialog de acción -->
    <v-dialog v-model="actionDialog" max-width="500" persistent>
      <v-card v-if="pendingAction">
        <v-card-title>{{ pendingAction.act.label }}</v-card-title>
        <v-card-subtitle>
          Incidencia #{{ pendingAction.incident.id }} — {{ pendingAction.incident.title }}
        </v-card-subtitle>

        <v-card-text>
          <!-- Resolución a validar (visible para accept/reject) -->
          <v-alert
            v-if="(pendingAction.act.action === 'accept_resolution' || pendingAction.act.action === 'reject_resolution') && pendingAction.incident.resolution"
            type="info"
            variant="tonal"
            class="mb-4"
            border="start"
          >
            <template #title>Resolución propuesta</template>
            {{ pendingAction.incident.resolution }}
          </v-alert>

          <!-- P13-V1C: selección de asignable para assign-user (advisory; el backend revalida) -->
          <v-select
            v-if="pendingAction.act.action === 'assign_user'"
            v-model="assignTargetId"
            :items="eligibleAssignees"
            :loading="assigneesLoading"
            item-title="name"
            item-value="id"
            label="Asignar a (jefes activos del departamento enrutado)"
            :rules="[v => !!v || 'Seleccione un usuario destino']"
          />

          <!-- Campo de resolución para submit_resolution -->
          <v-textarea
            v-if="requiresField(pendingAction.act, 'resolution')"
            v-model="actionResolution"
            label="Resolución"
            hint="Mínimo 20 caracteres"
            :rules="[v => (v && v.length >= 20) || 'La resolución debe tener al menos 20 caracteres']"
            counter
            rows="4"
          />

          <!-- Campo de comentario -->
          <v-textarea
            v-if="needsComment(pendingAction.act)"
            v-model="actionComment"
            :label="pendingAction.act.action === 'reopen' || pendingAction.act.action === 'reopen_incidence'
              ? 'Motivo de reapertura (mín. 20 caracteres)'
              : pendingAction.act.action === 'reject_resolution'
                ? 'Motivo del rechazo'
                : 'Comentario'"
            :hint="pendingAction.act.action === 'reopen' || pendingAction.act.action === 'reopen_incidence' ? 'Mínimo 20 caracteres' : ''"
            :rules="commentRules"
            counter
            rows="3"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelAction">Cancelar</v-btn>
          <v-btn
            :color="actionColor(pendingAction.act.action)"
            variant="flat"
            :loading="executing"
            @click="confirmAction"
          >
            {{ pendingAction.act.label }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail sheet (panel lateral) -->
    <v-navigation-drawer
      v-model="detailDrawer"
      location="right"
      temporary
      width="500"
    >
      <template v-if="selectedIncident">
        <v-card flat>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>#{{ selectedIncident.id }}</span>
            <v-btn icon="mdi-close" variant="text" @click="detailDrawer = false" />
          </v-card-title>
          <v-card-subtitle>{{ selectedIncident.title }}</v-card-subtitle>

          <v-card-text>
            <p class="text-body-2 mb-4">{{ selectedIncident.description }}</p>

            <v-chip :color="statusColor(selectedIncident.status)" class="mb-2" variant="flat">
              {{ statusLabel(selectedIncident.status) }}
            </v-chip>
            <v-chip :color="priorityColor(selectedIncident.priority)" class="mb-2 ml-2" variant="flat">
              {{ selectedIncident.priority }}
            </v-chip>

            <!-- Resolución prominente (para validación del creador) -->
            <v-alert
              v-if="selectedIncident.resolution"
              type="info"
              variant="tonal"
              class="mt-4"
              border="start"
            >
              <template #title>Resolución propuesta</template>
              {{ selectedIncident.resolution }}
            </v-alert>

            <v-list density="compact" class="mt-4">
              <v-list-item v-if="selectedIncident.assignedDepartment">
                <template #prepend><v-icon size="18">mdi-domain</v-icon></template>
                <v-list-item-title>{{ selectedIncident.assignedDepartment.name }}</v-list-item-title>
                <v-list-item-subtitle>Departamento asignado</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="selectedIncident.assignedUser">
                <template #prepend><v-icon size="18">mdi-account-check</v-icon></template>
                <v-list-item-title>{{ selectedIncident.assignedUser.name }}</v-list-item-title>
                <v-list-item-subtitle>Asignado a</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="selectedIncident.reportedByUser">
                <template #prepend><v-icon size="18">mdi-account-alert</v-icon></template>
                <v-list-item-title>{{ selectedIncident.reportedByUser.name }}</v-list-item-title>
                <v-list-item-subtitle>Reportado por</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <template #prepend><v-icon size="18">mdi-calendar</v-icon></template>
                <v-list-item-title>{{ formatDate(selectedIncident.createdAt) }}</v-list-item-title>
                <v-list-item-subtitle>Fecha de creación</v-list-item-subtitle>
              </v-list-item>
              <v-list-item v-if="selectedIncident.closedAt">
                <template #prepend><v-icon size="18">mdi-calendar-check</v-icon></template>
                <v-list-item-title>{{ formatDate(selectedIncident.closedAt) }}</v-list-item-title>
                <v-list-item-subtitle>Fecha de cierre</v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <!-- Acciones disponibles -->
            <div v-if="selectedIncident.availableActions?.length" class="mt-4">
              <p class="text-subtitle-2 mb-2">Acciones disponibles</p>
              <div class="d-flex flex-wrap ga-2">
                <v-btn
                  v-for="act in selectedIncident.availableActions"
                  :key="act.action"
                  :color="actionColor(act.action)"
                  variant="tonal"
                  size="small"
                  @click="openActionDialog(selectedIncident, act)"
                >
                  <v-icon start>{{ actionIcon(act.action) }}</v-icon>
                  {{ act.label }}
                </v-btn>
              </div>
            </div>

            <!-- Timeline de historia -->
            <div class="mt-6">
              <div class="d-flex align-center mb-2">
                <v-icon size="18" class="mr-2">mdi-timeline-text</v-icon>
                <p class="text-subtitle-2 mb-0">Historial</p>
                <v-btn
                  icon="mdi-refresh"
                  size="x-small"
                  variant="text"
                  class="ml-1"
                  :loading="historyLoading"
                  @click="loadHistory(selectedIncident!.id)"
                />
              </div>
              <v-progress-linear v-if="historyLoading" indeterminate color="primary" class="mb-2" />
              <v-timeline v-if="historyEntries.length > 0" density="compact" side="end" line-thickness="1">
                <v-timeline-item
                  v-for="entry in historyEntries"
                  :key="entry.id"
                  :dot-color="entryTypeColor(entry.entryType)"
                  size="x-small"
                >
                  <div class="text-body-2">
                    <span class="font-weight-medium">{{ entryTypeLabel(entry.entryType) }}</span>
                    <span v-if="entry.user" class="text-caption text-grey ml-1">
                      por {{ entry.user.name || entry.user.username }}
                    </span>
                  </div>
                  <div v-if="entry.comment" class="text-caption text-grey-darken-1 mt-1" style="white-space: pre-wrap;">
                    {{ entry.comment }}
                  </div>
                  <div class="text-caption text-grey">
                    {{ formatDate(entry.createdAt) }}
                  </div>
                </v-timeline-item>
              </v-timeline>
              <p v-else-if="!historyLoading" class="text-caption text-grey">Sin historial disponible</p>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </v-navigation-drawer>

    <!-- Lote 4.4 · P13-V1D: el diálogo consume SOLO la proyección del servidor que viaja en la
         incidencia (availableActions.escalate.constraints.allowedTargetRoles) — sin rol local -->
    <EscalateDialog
      v-model="escalateDialog"
      :incident="escalateTarget"
      @escalated="onEscalated"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  incidentInboxService,
  type InboxIncident,
  type InboxAction,
  type HistoryEntry,
} from '@/services/incidentInboxService'
// P13-V1C — operaciones nombradas del claim atómico (los botones provienen de availableActions,
// una proyección del SERVIDOR; este componente jamás calcula elegibilidad localmente).
import { incidentsService } from '@/services/incidentsService'
import type { EligibleAssignee } from '@/types/incidents'
import EscalateDialog from '@/components/incidents/EscalateDialog.vue'
// P13-V1D (C11): la bandeja consume las señales realtime del dominio con el MISMO contrato que
// las vistas de lista (payload = SEÑAL, no estado; refetch canónico server-scoped, coalescido).
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'
import { useTrailingRefetch } from '@/composables/useTrailingRefetch'

/**
 * P13-V1C — adaptador del contrato congelado: `requires` es un OBJETO {campo: boolean}.
 * Tolera defensivamente el array legado (jamás debería llegar) y cualquier forma extraña sin
 * reventar el render (el bug original: `.includes` sobre un objeto lanzaba TypeError DENTRO del
 * template y dejaba el diálogo en blanco).
 */
function requiresField(act: InboxAction | null | undefined, key: string): boolean {
  const req: unknown = act?.requires
  if (!req || typeof req !== 'object') return false
  if (Array.isArray(req)) return req.includes(key)
  return (req as Record<string, unknown>)[key] === true
}

// ═══════════════════════════════════════════════════════════
// Props
// ═══════════════════════════════════════════════════════════

// P13-V1D: el prop `userRole` fue RETIRADO — existía solo para alimentar la tabla local de pares
// del EscalateDialog (ningún mount lo pasaba: el diálogo era inerte). Los targets ahora viajan
// dentro de la incidencia como proyección del servidor; este componente no maneja rol alguno.
const props = withDefaults(defineProps<{
  /** Tipo de bandeja: 'mine' | 'department' | 'zone' | 'escalated' | 'agency' */
  inboxType: 'mine' | 'department' | 'zone' | 'escalated' | 'agency'
  /** ID de departamento (solo para type=department si se quiere forzar) */
  departmentId?: number
}>(), {
  inboxType: 'mine',
})

// ═══════════════════════════════════════════════════════════
// State
// ═══════════════════════════════════════════════════════════

const incidents = ref<InboxIncident[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const totalCount = ref(0)
const totalPages = ref(0)
const currentPage = ref(1)
const statusFilter = ref<string | null>(null)

// Detail panel
const selectedIncident = ref<InboxIncident | null>(null)
const detailDrawer = ref(false)

// Action dialog
const actionDialog = ref(false)
const pendingAction = ref<{ incident: InboxIncident; act: InboxAction } | null>(null)
const actionComment = ref('')
const actionResolution = ref('')
// P13-V1C — estado del assign-user (lista ADVISORY del servidor; jamás autoridad local)
const assignTargetId = ref<number | null>(null)
const eligibleAssignees = ref<EligibleAssignee[]>([])
const assigneesLoading = ref(false)
const executing = ref(false)

// History timeline
const historyEntries = ref<HistoryEntry[]>([])
const historyLoading = ref(false)

// Escalation dialog (Lote 4.4)
const escalateDialog = ref(false)
const escalateTarget = ref<InboxIncident | null>(null)

// ═══════════════════════════════════════════════════════════
// Computed
// ═══════════════════════════════════════════════════════════

const inboxTitle = computed(() => {
  switch (props.inboxType) {
    case 'mine': return 'Mis Incidencias'
    case 'department': return 'Incidencias del Departamento'
    case 'zone': return 'Incidencias de la Zona'
    case 'escalated': return 'Escaladas a mí'
    case 'agency': return 'Incidencias de mi agencia'
    default: return 'Bandeja'
  }
})

const statusOptions = [
  { label: 'Pendiente', value: 'pending' },
  { label: 'En Progreso', value: 'in_progress' },
  { label: 'Pendiente Validación', value: 'pending_validation' },
  { label: 'Cerrada', value: 'closed' },
  { label: 'Cancelada', value: 'cancelled' },
]

const commentRules = computed(() => {
  if (!pendingAction.value) return []
  const act = pendingAction.value.act
  if (requiresField(act, 'comment')) {
    if (act.action === 'reopen' || act.action === 'reopen_incidence') {
      return [(v: string) => (v && v.length >= 20) || 'El motivo debe tener al menos 20 caracteres']
    }
    return [(v: string) => !!v || 'El comentario es requerido']
  }
  // reject_resolution always requires comment per spec
  if (act.action === 'reject_resolution') {
    return [(v: string) => !!v || 'Debe indicar el motivo del rechazo']
  }
  return []
})

// ═══════════════════════════════════════════════════════════
// Data loading
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════════════════
// P13-V1D BOUNDED REPAIR R1 (F3/F4) — UNA SOLA AUTORIDAD DE LECTURA DEL INBOX.
//
// TODA lectura capaz de reemplazar `incidents.value` pasa por UN coordinador
// (`requestInboxRefresh`): mount, refresh manual, watchers de filtro/paginación, post-acción,
// señales realtime y reconnect. El raw fetch (`fetchInboxSnapshot`) es privado, no muta estado
// y NO se expone. Cada request captura (a) un número de GENERACIÓN (solo la request más
// recientemente INICIADA puede aplicar su snapshot — una respuesta superseded se DESCARTA, su
// error no pisa estado vigente) y (b) la BARRERA DE REVOCACIÓN vigente (`incident:access-revoked`
// la incrementa: ninguna respuesta iniciada ANTES de la revocación puede rematerializar la fila;
// una request iniciada DESPUÉS captura la barrera nueva y puede volver a mostrarla únicamente si
// el backend server-scoped la incluye — recuperación legítima, sin tombstone permanente).
// ═══════════════════════════════════════════════════════════════════════════════════════════

let inboxReadSeq = 0
let inboxRevocationBarrier = 0

async function fetchInboxSnapshot() {
  const filters: any = {
    page: currentPage.value,
    limit: 50,
  }
  if (statusFilter.value) {
    filters.status = statusFilter.value
  }
  switch (props.inboxType) {
    case 'mine':
      return incidentInboxService.getMine(filters)
    case 'department':
      if (props.departmentId) filters.departmentId = props.departmentId
      return incidentInboxService.getDepartment(filters)
    case 'zone':
      return incidentInboxService.getZone(filters)
    case 'escalated':
      return incidentInboxService.getEscalated(filters)
    // P13-G-DEMO-PREQ-DEF01 — bandeja OPERATIVA de la agencia: listado canónico particionado por el
    // servidor al point asignado, con independencia de quién reportó. Es la única lectura del
    // componente que puede materializar la fila del Scenario B (reporter != validadora).
    case 'agency':
      return incidentInboxService.getAgency(filters)
    default:
      // Un inbox-type desconocido debe FALLAR, nunca degradar a bandeja vacía: `requestInboxRefresh`
      // descarta un snapshot `undefined` (`if (response)`), así que sin este throw un prop mal
      // escrito renderiza una bandeja vacía indistinguible de "no hay incidencias" — el modo de
      // fallo exacto que un demo enviaría sin darse cuenta.
      throw new Error(`inboxType no soportado: ${props.inboxType}`)
  }
}

async function requestInboxRefresh(reason: string = 'manual') {
  const gen = ++inboxReadSeq
  const barrier = inboxRevocationBarrier
  loading.value = true
  error.value = null
  try {
    const response = await fetchInboxSnapshot()
    if (gen !== inboxReadSeq || barrier !== inboxRevocationBarrier) return // superseded / pre-revocación → descartar
    if (response) {
      // ÚNICA región que asigna el snapshot completo — protegida por el guard de arriba.
      incidents.value = response.data
      totalCount.value = response.count
      totalPages.value = response.totalPages
    }
  } catch (err: any) {
    if (gen !== inboxReadSeq || barrier !== inboxRevocationBarrier) return // fallo stale: no pisa estado vigente
    error.value = err?.response?.data?.error || err?.message || 'Error cargando bandeja'
    console.error(`❌ Error en inbox/${props.inboxType} (${reason}):`, err)
  } finally {
    if (gen === inboxReadSeq) loading.value = false
  }
}

// Alias público: TODO trigger (template, watchers, post-acción, expose) entra por el coordinador.
const loadData = (reason: string = 'manual') => requestInboxRefresh(reason)

// ═══════════════════════════════════════════════════════════
// Actions
// ═══════════════════════════════════════════════════════════

function openActionDialog(incident: InboxIncident, act: InboxAction) {
  // Lote 4.4: Redirigir acción escalate al EscalateDialog
  if (act.action === 'escalate') {
    escalateTarget.value = incident
    escalateDialog.value = true
    return
  }
  // Lote 4.4: Acknowledge escalation inline
  if (act.action === 'acknowledge_escalation') {
    handleAcknowledgeEscalation(incident)
    return
  }
  pendingAction.value = { incident, act }
  actionComment.value = ''
  actionResolution.value = ''
  // P13-V1C: assign-user precarga los asignables elegibles (lista ADVISORY del servidor; la
  // operación revalida al destino dentro de su transacción — la lista jamás concede autoridad).
  assignTargetId.value = null
  eligibleAssignees.value = []
  if (act.action === 'assign_user') {
    assigneesLoading.value = true
    incidentsService.getEligibleAssignees(incident.id)
      .then((list) => { eligibleAssignees.value = list })
      .catch((err) => { error.value = err?.response?.data?.error || 'Error cargando asignables' })
      .finally(() => { assigneesLoading.value = false })
  }
  actionDialog.value = true
}

function cancelAction() {
  actionDialog.value = false
  pendingAction.value = null
  actionComment.value = ''
  actionResolution.value = ''
}

function needsComment(act: InboxAction): boolean {
  return requiresField(act, 'comment') || act.action === 'accept_resolution' || act.action === 'reject_resolution'
}

async function confirmAction() {
  if (!pendingAction.value) return

  const { incident, act } = pendingAction.value
  executing.value = true

  try {
    // P13-V1C — operaciones nombradas del claim atómico (nunca el endpoint de status).
    if (act.action === 'claim') {
      await incidentsService.claimIncident(incident.id)
    } else if (act.action === 'release') {
      await incidentsService.releaseIncident(incident.id)
    } else if (act.action === 'assign_user') {
      if (!assignTargetId.value) {
        error.value = 'Seleccione un usuario destino'
        executing.value = false
        return
      }
      await incidentsService.assignIncidentUser(incident.id, assignTargetId.value)
    // Usar endpoints canónicos del Lote 4.3 para T6/T7/T8
    } else if (act.action === 'accept_resolution') {
      await incidentInboxService.validateResolution(incident.id, true, actionComment.value || undefined)
    } else if (act.action === 'reject_resolution') {
      await incidentInboxService.validateResolution(incident.id, false, actionComment.value)
    } else if (act.action === 'reopen_incidence') {
      await incidentInboxService.reopen(incident.id, actionComment.value)
    } else {
      // Demás acciones (T1-T5) siguen usando el endpoint unificado
      const payload: { comment?: string; resolution?: string } = {}
      if (actionComment.value) payload.comment = actionComment.value
      if (actionResolution.value) payload.resolution = actionResolution.value
      await incidentInboxService.executeAction(incident.id, act.action, payload)
    }

    // Cerrar dialog y recargar datos
    cancelAction()
    await loadData()
  } catch (err: any) {
    error.value = err?.response?.data?.error || err?.message || 'Error ejecutando acción'
    console.error('❌ Error ejecutando acción:', err)
  } finally {
    executing.value = false
  }
}

// ═══════════════════════════════════════════════════════════
// UI Helpers
// ═══════════════════════════════════════════════════════════

function priorityColor(priority: string): string {
  const colors: Record<string, string> = {
    critical: 'red-darken-2', CRITICAL: 'red-darken-2',
    high: 'orange-darken-1', HIGH: 'orange-darken-1',
    medium: 'amber', MEDIUM: 'amber',
    low: 'green', LOW: 'green',
  }
  return colors[priority] || 'grey'
}

function priorityIcon(priority: string): string {
  const icons: Record<string, string> = {
    critical: 'mdi-alert-octagon', CRITICAL: 'mdi-alert-octagon',
    high: 'mdi-alert', HIGH: 'mdi-alert',
    medium: 'mdi-alert-circle', MEDIUM: 'mdi-alert-circle',
    low: 'mdi-information', LOW: 'mdi-information',
  }
  return icons[priority] || 'mdi-help-circle'
}

function statusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'blue-lighten-1',
    in_progress: 'orange',
    pending_validation: 'purple',
    closed: 'green',
    cancelled: 'grey',
  }
  return colors[status] || 'grey'
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pendiente',
    in_progress: 'En Progreso',
    pending_validation: 'Pendiente Validación',
    closed: 'Cerrada',
    cancelled: 'Cancelada',
  }
  return labels[status] || status
}

function actionIcon(action: string): string {
  const icons: Record<string, string> = {
    start_work: 'mdi-play-circle',
    cancel: 'mdi-cancel',
    cancel_incidence: 'mdi-cancel',
    submit_resolution: 'mdi-check-circle',
    return_to_triage: 'mdi-undo',
    accept_resolution: 'mdi-thumb-up',
    reject_resolution: 'mdi-thumb-down',
    reopen: 'mdi-refresh',
    reopen_incidence: 'mdi-refresh',
    escalate: 'mdi-arrow-up-bold',
    acknowledge_escalation: 'mdi-check-decagram',
  }
  return icons[action] || 'mdi-cog'
}

function actionColor(action: string): string {
  const colors: Record<string, string> = {
    start_work: 'primary',
    cancel: 'error',
    cancel_incidence: 'error',
    submit_resolution: 'success',
    return_to_triage: 'warning',
    accept_resolution: 'success',
    reject_resolution: 'error',
    reopen: 'info',
    reopen_incidence: 'info',
    escalate: 'warning',
    acknowledge_escalation: 'teal',
  }
  return colors[action] || 'grey'
}

function formatDate(date: string): string {
  if (!date) return ''
  return new Date(date).toLocaleString('es-DO', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ═══════════════════════════════════════════════════════════
// History / Timeline (Lote 4.3)
// ═══════════════════════════════════════════════════════════

async function loadHistory(incidentId: number) {
  historyLoading.value = true
  try {
    historyEntries.value = await incidentInboxService.getHistory(incidentId)
  } catch (err: any) {
    console.error('❌ Error cargando historial:', err)
    historyEntries.value = []
  } finally {
    historyLoading.value = false
  }
}

function entryTypeColor(entryType: string): string {
  const colors: Record<string, string> = {
    creation: 'blue',
    status_change: 'primary',
    resolution: 'success',
    closure: 'green-darken-2',
    rejection: 'error',
    cancellation: 'grey-darken-1',
    return_to_triage: 'warning',
    reopen: 'info',
    assignment: 'purple',
    transfer: 'deep-purple',
    escalation: 'warning',
    escalation_ack: 'teal',
  }
  return colors[entryType] || 'grey'
}

function entryTypeLabel(entryType: string): string {
  const labels: Record<string, string> = {
    creation: 'Creada',
    status_change: 'Trabajo iniciado',
    resolution: 'Resolución enviada',
    closure: 'Cerrada',
    rejection: 'Resolución rechazada',
    cancellation: 'Cancelada',
    return_to_triage: 'Devuelta a triage',
    reopen: 'Reabierta',
    assignment: 'Asignada',
    transfer: 'Transferida',
    escalation: 'Escalada',
    escalation_ack: 'Escalación reconocida',
  }
  return labels[entryType] || entryType || 'Cambio de estado'
}

// ═══════════════════════════════════════════════════════════
// Watchers & Lifecycle
// ═══════════════════════════════════════════════════════════

watch(selectedIncident, (val) => {
  detailDrawer.value = !!val
  if (val) {
    loadHistory(val.id)
  } else {
    historyEntries.value = []
  }
})

watch(detailDrawer, (val) => {
  if (!val) selectedIncident.value = null
})

watch(statusFilter, () => {
  currentPage.value = 1
  loadData()
})

// ═══════════════════════════════════════════════════════════
// Escalation Handlers (Lote 4.4)
// ═══════════════════════════════════════════════════════════

async function handleAcknowledgeEscalation(incident: InboxIncident) {
  const escalations = (incident as any).activeEscalations
  if (!escalations || escalations.length === 0) {
    error.value = 'No hay escalaciones activas para reconocer'
    return
  }
  executing.value = true
  try {
    // Reconocer la primera escalación activa
    await incidentInboxService.acknowledgeEscalation(incident.id, escalations[0].id)
    await loadData()
  } catch (err: any) {
    error.value = err?.response?.data?.error || err?.message || 'Error al reconocer escalación'
  } finally {
    executing.value = false
  }
}

function onEscalated() {
  loadData()
}

// ═══════════════════════════════════════════════════════════════════════════════════════════
// P13-V1D (C11) — señales realtime de la bandeja. Contrato idéntico al de las vistas de lista:
// el payload es SEÑAL, no estado — toda actualización visible proviene de un refetch canónico
// server-scoped, coalescido en trailing-edge (la última señal de una ráfaga nunca se pierde);
// `incident:access-revoked` SOLO remueve/cierra (sin refetch del subject) — un frame viejo no
// rematerializa nada porque la lista solo se puebla desde el servidor.
//
// EXCEPCIÓN FROZEN (decisión #10): la bandeja ESCALATED es QUERY_DRIVEN — carga al montar, tras
// acción local y con refresh manual. NO registra señal alguna (el refetch por señal de
// notificación exigiría prueba de consumo del evento ACTIVE `realtime:notification`, no provista;
// ninguna promesa de frescura no probada).
// ═══════════════════════════════════════════════════════════════════════════════════════════

const refetchInboxCoalesced = useTrailingRefetch(() => requestInboxRefresh('realtime-signal'))

const subjectIdOf = (data: any): number | null => {
  const n = Number(data?.incidenceId)
  return Number.isInteger(n) && n > 0 ? n : null
}

const handleRealtimeSignal = async (data: any) => {
  if (subjectIdOf(data) === null) return
  await refetchInboxCoalesced()
}

const handleRealtimeAccessRevoked = (data: any) => {
  const id = subjectIdOf(data)
  if (id === null) return
  // P13-V1D R1 (F3): la revocación levanta la BARRERA — toda respuesta iniciada ANTES de este
  // instante queda invalidada y no puede rematerializar la fila (no basta filtrar la lista
  // actual: un loadData en vuelo cuyo snapshot precede la revocación la reinsertaría).
  inboxRevocationBarrier += 1
  incidents.value = incidents.value.filter((inc) => Number(inc.id) !== id)
  if (selectedIncident.value && Number(selectedIncident.value.id) === id) {
    selectedIncident.value = null
    detailDrawer.value = false
  }
  // P13-V1D R2 (B2): la barrera invalida — y el estado se RECONCILIA: una lectura server-scoped
  // POST-barrera vía el coordinador (coalescida trailing-edge: una ráfaga de revocaciones produce
  // UNA lectura terminal). Sin esto, un refresh legítimo invalidado (filtro/página/manual/señal)
  // se perdería y el snapshot previo quedaría stale indefinidamente.
  void refetchInboxCoalesced()
}

const handleRealtimeReconnect = async () => {
  // Resincronización tras reconexión (sin replay: la verdad es el servidor).
  await refetchInboxCoalesced()
}

const registersRealtime = props.inboxType !== 'escalated'

onMounted(() => {
  loadData()
  if (registersRealtime) {
    websocketService.on(RT_LOCAL.INCIDENT_CREATED, handleRealtimeSignal)
    websocketService.on(RT_LOCAL.INCIDENT_DEPARTMENT_ASSIGNED, handleRealtimeSignal)
    websocketService.on(RT_LOCAL.INCIDENT_HANDLER_CHANGED, handleRealtimeSignal)
    websocketService.on(RT_LOCAL.INCIDENT_STATUS_CHANGED, handleRealtimeSignal)
    websocketService.on(RT_LOCAL.INCIDENT_ACCESS_REVOKED, handleRealtimeAccessRevoked)
    websocketService.on(RT_LOCAL.SOCKET_RECONNECT, handleRealtimeReconnect)
  }
})

onUnmounted(() => {
  if (registersRealtime) {
    websocketService.off(RT_LOCAL.INCIDENT_CREATED, handleRealtimeSignal)
    websocketService.off(RT_LOCAL.INCIDENT_DEPARTMENT_ASSIGNED, handleRealtimeSignal)
    websocketService.off(RT_LOCAL.INCIDENT_HANDLER_CHANGED, handleRealtimeSignal)
    websocketService.off(RT_LOCAL.INCIDENT_STATUS_CHANGED, handleRealtimeSignal)
    websocketService.off(RT_LOCAL.INCIDENT_ACCESS_REVOKED, handleRealtimeAccessRevoked)
    websocketService.off(RT_LOCAL.SOCKET_RECONNECT, handleRealtimeReconnect)
  }
})

defineExpose({ loadData })
</script>
